import { Component, type ErrorInfo, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { reportError } from "@/shared/sentry";

// Per-route error boundary scoped to a single page's content area. Sits
// inside AppShell around the <Outlet />, so a render error in one panel
// doesn't take down the top nav - users can switch tabs and recover instead
// of seeing the app-wide fallback in main.tsx that blanks everything.
//
// Resets internal state when the route path changes so navigating away
// clears the failure without the user having to click "Try again".

interface InnerProps {
  children: ReactNode;
  routeKey: string;
}

interface InnerState {
  hasError: boolean;
  message: string | null;
}

class RouteErrorBoundaryInner extends Component<InnerProps, InnerState> {
  state: InnerState = { hasError: false, message: null };

  static getDerivedStateFromError(error: unknown): InnerState {
    const message = error instanceof Error ? error.message : "Something went wrong.";
    return { hasError: true, message };
  }

  componentDidUpdate(prevProps: InnerProps) {
    if (prevProps.routeKey !== this.props.routeKey && this.state.hasError) {
      this.setState({ hasError: false, message: null });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    reportError(error);
    console.error("Route render error", error, info);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="route-error-state" role="alert">
        <h2>This section couldn't load.</h2>
        <p>
          {this.state.message ??
            "An unexpected error happened while rendering this page."}
        </p>
        <div className="route-error-actions">
          <button
            className="route-error-btn-primary"
            onClick={() => this.setState({ hasError: false, message: null })}
            type="button"
          >
            Try again
          </button>
          <button
            className="route-error-btn-secondary"
            onClick={() => window.location.reload()}
            type="button"
          >
            Reload the page
          </button>
        </div>
      </div>
    );
  }
}

/**
 * Wraps the inner class component and keys it by the current pathname so
 * navigations clear the error automatically.
 */
export function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  return (
    <RouteErrorBoundaryInner routeKey={location.pathname}>
      {children}
    </RouteErrorBoundaryInner>
  );
}
